// components/TemplateModal.jsx
import React, { useState, useEffect } from 'react';
import { FiX, FiSave, FiEdit2 } from 'react-icons/fi';
import { toast } from 'react-toastify';

const TemplateModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  template = null,
  mode = 'create'
}) => {
  const [formData, setFormData] = useState({
    name: '',
    content: ''
  });
  const [loading, setLoading] = useState(false);

  // FIXED: Add early return and use template?.id
  useEffect(() => {
    if (!isOpen) return; // Early return when modal is closed
    
    if (template && mode === 'edit') {
      setFormData({
        name: template.name || '',
        content: template.content || ''
      });
    } else {
      setFormData({
        name: '',
        content: ''
      });
    }
  }, [isOpen, mode, template?.id]); // Use template?.id instead of template

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    
    if (!formData.content.trim()) {
      toast.error('Template content is required');
      return;
    }

    setLoading(true);
    try {
      await onSave(formData, template?.id);
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInsertVariable = (variable) => {
    const textarea = document.getElementById('template-content');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const textBefore = formData.content.substring(0, start);
    const textAfter = formData.content.substring(end);
    const newContent = textBefore + variable + textAfter;

    setFormData(prev => ({
      ...prev,
      content: newContent
    }));

    // Focus back on textarea
    setTimeout(() => {
      textarea.focus();
      const newCursorPosition = start + variable.length;
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-50 rounded-xl border border-red-200">
              {mode === 'edit' ? (
                <FiEdit2 className="text-xl text-red-600" />
              ) : (
                <FiSave className="text-xl text-red-600" />
              )}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">
                {mode === 'edit' ? 'Edit Template' : 'Create Custom Template'}
              </h3>
              <p className="text-sm text-gray-600">
                {mode === 'edit' ? 'Update your template details' : 'Add a new custom SMS template'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="p-6 space-y-6">
            {/* Template Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Welcome Message"
                className="w-full px-4 py-3 text-gray-700 bg-gray-50 rounded-xl border-2 border-gray-200 transition-all outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
                required
              />
            </div>

            {/* Variables Section */}
            <div>
              <div className="flex flex-wrap gap-2 items-center mb-2">
                <span className="text-sm font-medium text-gray-600">Insert Variables:</span>
                <button
                  type="button"
                  onClick={() => handleInsertVariable('{{first_name}}')}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                >
                  First Name
                </button>
                <button
                  type="button"
                  onClick={() => handleInsertVariable('{{last_name}}')}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                >
                  Last Name
                </button>
                <button
                  type="button"
                  onClick={() => handleInsertVariable('{{full_name}}')}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                >
                  Full Name
                </button>
                <button
                  type="button"
                  onClick={() => handleInsertVariable('{{phone_number}}')}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                >
                  Phone Number
                </button>
                <button
                  type="button"
                  onClick={() => handleInsertVariable('{{email}}')}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                >
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => handleInsertVariable('{{code}}')}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                >
                  Code
                </button>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs font-medium text-blue-700">
                  Variables will be replaced with actual contact data when sending messages
                </p>
              </div>
            </div>

            {/* Template Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template Content *
              </label>
              <textarea
                id="template-content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                placeholder="Enter your template content here..."
                rows={6}
                className="w-full px-4 py-3 text-gray-700 bg-gray-50 rounded-xl border-2 border-gray-200 transition-all outline-none resize-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
                required
              />
              <div className="mt-2 text-right text-sm text-gray-500">
                {formData.content.length}/160 characters
              </div>
            </div>

            {/* Preview */}
            {formData.content && (
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Preview:</h4>
                <div className="p-3 bg-white rounded-lg border border-gray-300">
                  <p className="text-gray-700 whitespace-pre-wrap">{formData.content}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 text-white bg-red-600 rounded-xl font-medium hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {mode === 'edit' ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" />
                  {mode === 'edit' ? 'Update Template' : 'Create Template'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateModal;